<template>
  <div class="bookmarked-posts container mx-auto px-4 py-8">
    <h1 class="text-3xl font-bold mb-8 text-gray-800 dark:text-gray-200">Your Bookmarked Posts</h1>
    
    <div v-if="loading" class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
    </div>
    
    <div v-else-if="error" class="bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 p-4 rounded-lg mb-6">
      {{ error }}
    </div>
    
    <div v-else-if="bookmarkedPosts.length === 0" class="text-center py-12">
      <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mx-auto text-gray-400 dark:text-gray-600 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
      </svg>
      <h2 class="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">No bookmarked posts yet</h2>
      <p class="text-gray-600 dark:text-gray-400 mb-6">Bookmark posts you want to read later, and they'll appear here for easy access.</p>
      <router-link to="/" class="inline-block bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200">
        Explore Posts
      </router-link>
    </div>
    
    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <BlogCard v-for="post in bookmarkedPosts" :key="post.id" :post="post" />
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import BlogCard from '@/components/BlogCard.vue'

export default {
  name: 'BookmarkedPosts',
  components: {
    BlogCard
  },
  setup() {
    const store = useStore()
    const loading = ref(true)
    const error = ref(null)
    const bookmarkedPosts = ref([])
    
    const isAuthenticated = computed(() => store.getters.isAuthenticated)
    
    const fetchBookmarkedPosts = async () => {
      if (!isAuthenticated.value) {
        error.value = 'You need to be logged in to view your bookmarked posts'
        loading.value = false
        return
      }
      
      try {
        loading.value = true
        error.value = null
        
        // Get user's bookmarked post IDs
        const bookmarkedPostIds = await store.dispatch('fetchUserBookmarks')
        
        // Fetch the actual post data for each bookmarked post
        const posts = []
        for (const postId of bookmarkedPostIds) {
          try {
            const post = await store.dispatch('fetchPost', postId)
            if (post) {
              posts.push(post)
            }
          } catch (err) {
            console.error(`Error fetching post ${postId}:`, err)
            // Continue with other posts even if one fails
          }
        }
        
        bookmarkedPosts.value = posts
      } catch (err) {
        console.error('Error fetching bookmarked posts:', err)
        error.value = 'Failed to load bookmarked posts. Please try again later.'
      } finally {
        loading.value = false
      }
    }
    
    onMounted(fetchBookmarkedPosts)
    
    // Watch for changes in userLikes and userBookmarks
    const userLikes = computed(() => store.state.userLikes)
    const userBookmarks = computed(() => store.state.userBookmarks)
    
    // Update post like status when userLikes changes
    watch(userLikes, (newLikes) => {
      bookmarkedPosts.value.forEach(post => {
        post.isLiked = newLikes.includes(post.id)
      })
    })
    
    // Update post bookmark status when userBookmarks changes
    watch(userBookmarks, (newBookmarks) => {
      // If a post is unbookmarked, we need to remove it from the list
      if (bookmarkedPosts.value.length > 0) {
        bookmarkedPosts.value = bookmarkedPosts.value.filter(post => 
          newBookmarks.includes(post.id)
        )
        
        // Update isBookmarked flag for remaining posts
        bookmarkedPosts.value.forEach(post => {
          post.isBookmarked = true // All posts in this view should be bookmarked
        })
      }
    })
    
    return {
      loading,
      error,
      bookmarkedPosts,
      isAuthenticated
    }
  }
}
</script>
